import { useModal } from "@ebay/nice-modal-react";
import { useCallback } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import FormFieldSpace from "components/FormFields/Space";
import FormFieldTags from "components/FormFields/Tags";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerSection from "ds/components/Drawer/Section";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import useCreateSpaceliftVcsRepo from "shared/SpaceliftVcsRepo/useCreateSpaceliftVcsRepo";
import useSpacesByAction from "shared/Space/useSpacesByAction";
import { Action } from "types/generated";
import { getDrawerFormFix } from "utils/css";
import { stringIsRequired } from "utils/formValidators";

type CreateSpaceliftVcsRepoFormFields = {
  name: string;
  description: string;
  space: string;
  labels: { value: string }[];
};

const CreateSpaceliftVcsRepoDrawer = createDrawer(() => {
  const drawer = useModal();
  const { createSpaceliftVcsRepo, loading } = useCreateSpaceliftVcsRepo();
  const { spaces: creatableSpaces, loading: spacesLoading } = useSpacesByAction({
    actions: [Action.SpaceliftVcsCreate],
  });

  const form = useForm<CreateSpaceliftVcsRepoFormFields>({
    defaultValues: {
      name: "",
      description: "",
      space: "",
      labels: [],
    },
    mode: "onChange",
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = form;

  const onSubmit: SubmitHandler<CreateSpaceliftVcsRepoFormFields> = useCallback(
    (data) => {
      createSpaceliftVcsRepo(
        {
          name: data.name,
          description: data.description || null,
          spaceID: data.space,
          labels: data.labels.length > 0 ? data.labels.map((l) => l.value) : null,
          isDefault: null,
          vcsChecks: null,
        },
        () => drawer.hide()
      );
    },
    [createSpaceliftVcsRepo, drawer]
  );

  return (
    <DrawerForm isDirty={isDirty}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)} {...getDrawerFormFix()}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle title="Create new repo" />
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody fullHeight noPadding>
            <DrawerSection gap="x-large">
              <FormField label="Name" error={errors?.name?.message} noMargin>
                {({ ariaInputProps }) => (
                  <Input
                    placeholder="Enter repo name"
                    error={!!errors?.name}
                    {...register("name", {
                      validate: stringIsRequired(),
                    })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>

              <FormField label="Description" error={errors?.description?.message} noMargin>
                {({ ariaInputProps }) => (
                  <Textarea
                    placeholder="Enter description (optional)"
                    rows={4}
                    {...register("description")}
                    {...ariaInputProps}
                  />
                )}
              </FormField>

              <FormFieldSpace noMargin spaces={creatableSpaces} loading={spacesLoading} />

              <FormFieldTags label="Labels" tagName="label" name="labels" isOptional noMargin />
            </DrawerSection>
          </DrawerBody>
          <DrawerFooter>
            <DrawerFooterActions>
              <DrawerCancelButton />
              <Button type="submit" variant="primary" loading={loading} disabled={!isValid}>
                Create
              </Button>
            </DrawerFooterActions>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerForm>
  );
});

export const showCreateSpaceliftVcsRepoDrawer = createDrawerTrigger(CreateSpaceliftVcsRepoDrawer);
